import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Hero variant picker.
 *
 * - If `slug` (from a geo landing page) is provided and matches a known city → uses that.
 * - Otherwise calls /api/geo/detect once per session (cached in sessionStorage) to
 *   pick the closest variant to the visitor's IP-derived location.
 * - Falls back to "miami" if anything errors.
 *
 * Also persists the chosen variant + detected city into localStorage so ad tracking
 * beacons can attribute conversions back to a hero.
 */
const CACHE_KEY = "bb_hero_detect_v1";

export const HERO_IMAGES = {
  miami: "/hero-miami.png",
  vegas: "/hero-vegas.png",
};

export function useHeroVariant(slug) {
  const [state, setState] = useState(() => {
    // Try cache first for instant paint
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
      if (cached && HERO_IMAGES[cached.hero_variant]) return cached;
    } catch {}
    return { hero_variant: "miami", source: "default" };
  });

  useEffect(() => {
    const url = slug ? `/geo/detect?slug=${encodeURIComponent(slug)}` : "/geo/detect";
    api.get(url)
      .then((r) => {
        const data = r.data;
        if (!HERO_IMAGES[data.hero_variant]) data.hero_variant = "miami";
        setState(data);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
          // Expose to ad tracking + cart events
          localStorage.setItem("bb_hero_shown", data.hero_variant);
          if (data.city) localStorage.setItem("bb_detected_city", data.city);
          if (data.region) localStorage.setItem("bb_detected_region", data.region);
        } catch (err) {
          console.error("Hero variant cache write failed:", err);
        }
      })
      .catch((err) => console.error("Hero detect failed:", err));
  }, [slug]);

  return { ...state, image: HERO_IMAGES[state.hero_variant] || HERO_IMAGES.miami };
}
